import { NgClass, ViewportScroller } from '@angular/common';
import {
  Component,
  input,
  output,
  signal,
  OnDestroy,
  effect,
  AfterViewInit
} from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { featherX } from '@ng-icons/feather-icons';
import { filter, Subject, takeUntil } from 'rxjs';

import { ScreenService } from '@app/core/services/screen.service';

interface NavPage {
  pageName: string;
  link: string;
}

const MOBILE_BREAKPOINT = 768;

@Component({
  standalone: true,
  selector: 'mw-store-navbar',
  imports: [NgClass, NgIconComponent, RouterModule],
  providers: [provideIcons({ featherX })],
  templateUrl: './store-navbar.component.html',
  styleUrl: './store-navbar.component.scss'
})
export class StoreNavbarComponent implements AfterViewInit, OnDestroy {
  readonly isCollapsed = input.required<boolean>();
  readonly toggleCollapse = output<void>();

  navPages: NavPage[] = [
    { pageName: 'Biografia', link: 'biography' },
    { pageName: 'Comprar Agora', link: 'shop' },
    { pageName: 'Recados', link: 'messages' }
  ];

  protected activeSection = signal<string | null>(null);

  private observer: IntersectionObserver | null = null;
  private destroy$ = new Subject<void>();
  private timeoutIds = new Set<NodeJS.Timeout>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private viewportScroller: ViewportScroller,
    private windowResize: ScreenService
  ) {
    this.setupFragmentNavigation();

    effect(() => {
      const body = document.body;
      const isMobile = this.windowResize.isMobileDevice(MOBILE_BREAKPOINT);

      if (!this.isCollapsed() && isMobile) {
        const hasScrollbar =
          window.innerWidth > document.documentElement.clientWidth;
        if (hasScrollbar) {
          const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth;
          body.style.paddingRight = `${scrollbarWidth}px`;
        }
        body.classList.add('overflow-hidden');
      } else {
        body.classList.remove('overflow-hidden');
        body.style.paddingRight = '';
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.observer?.disconnect();

    this.timeoutIds.forEach((id) => clearTimeout(id));
  }

  navigateToSection(sectionId: string): void {
    if (!this.isCollapsed()) {
      this.toggleCollapse.emit();
    }

    this.router.navigate([], {
      fragment: sectionId,
      replaceUrl: true
    });
  }

  private setupFragmentNavigation(): void {
    this.route.fragment
      .pipe(
        takeUntil(this.destroy$),
        filter((fragment) => !!fragment)
      )
      .subscribe((fragment) => {
        if (fragment) {
          this.activeSection.set(fragment);
          this.scrollToFragment(fragment);

          if (this.activeSection()) {
            this.toggleCollapse.emit();
          }
        }
      });
  }

  private scrollToFragment(fragment: string): void {
    const element = document.getElementById(fragment);

    if (!element) {
      console.warn(`Element with id "${fragment}" not found.`);
      return;
    }

    const header = document.getElementById('header');

    if (!header) {
      console.warn('Header element not found.');
      return;
    }

    this.setSafeTimeout(() => {
      const offset = element.offsetTop - (header?.offsetHeight || 0);
      this.viewportScroller.scrollToPosition([0, offset]);
    });
  }

  private setSafeTimeout(callback: () => void, delay = 50) {
    const id = setTimeout(() => {
      callback();
      this.timeoutIds.delete(id);
    }, delay);

    this.timeoutIds.add(id);
    return id;
  }

  private setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.75
    };

    const intersectionCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.activeSection.set(entry.target.id);
        }
      });
    };

    this.observer = new IntersectionObserver(intersectionCallback, options);
    this.navPages.forEach((page) => {
      const element = document.getElementById(page.link ?? '');
      if (element) {
        this.observer?.observe(element);
      }
    });
  }
}
